'use client'

import React, { ComponentType, ReactNode } from 'react'
import { widgetsCdn } from '@/components/widgets/widgetsCdn'
import { isProductionEnv } from 'shared/envHelpers'
import { ParsedUrlQuery } from 'querystring'

type WidgetInfoContainerProps = {
  htmlContainerId: string
  widgetName: string
  teamId?: string
  clubId?: string
  tournamentId?: string
  organizationId?: string
  tag?: string
  children: ReactNode
  query?: ParsedUrlQuery
}

const WidgetInfoItem: ComponentType<{
  index: number
  text: string
  code: string
}> = ({ index, text, code }) => (
  <div className="view-vertical space-y-2 mb-4">
    <span>
      <b>{index}.</b> {text}
    </span>
    <code className="text-xs break-words whitespace-pre-line">{code}</code>
  </div>
)

const WidgetInfoContainer: ComponentType<WidgetInfoContainerProps> = ({
  htmlContainerId,
  widgetName,
  teamId,
  clubId,
  tournamentId,
  organizationId,
  tag,
  children,
  query
}) => {
  if (!teamId && !clubId && !tournamentId && !organizationId && !tag) {
    throw new Error('TeamId, tournamentId, organizationId or tag is missing')
  }
  const cdn = isProductionEnv() ? widgetsCdn.production : widgetsCdn.staging
  const paramKey = teamId
    ? 'teamId'
    : clubId
    ? 'clubId'
    : tournamentId
    ? 'tournamentId'
    : tag
    ? 'tag'
    : 'organizationId'
  const paramValue = teamId || clubId || organizationId || tournamentId || tag
  const season = query && query.season ? (query.season as string) : null
  const phase = query && query.phase ? (query.phase as string) : null

  let codeSnippet
  if (isProductionEnv()) {
    codeSnippet = `<script>
      _hb({
      widget: '${widgetName}',
      ${paramKey}: '${paramValue}',
      container: '${htmlContainerId}'${
      season ? ",\nseason: '" + season + "'" : ''
    }${phase ? ",\nphase: '" + phase + "'" : ''}
    })
    </script>`
  } else {
    codeSnippet = `<script>
      _hb({
      env: 'staging',
      widget: '${widgetName}',
      ${paramKey}: '${paramValue}',
      container: '${htmlContainerId}'${
      season ? ",\nseason: '" + season + "'" : ''
    }${phase ? ",\nphase: '" + phase + "'" : ''}
    })
    </script>`
  }

  return (
    <>
      <div className="mb-8">
        <h2>So sieht das Widget auf Deiner Website aus (*):</h2>
        <div className="border-2 p-4 overflow-auto">{children}</div>
        <div className="view-vertical space-y-2 text-xs">
          <div className="flex flex-row mt-5">
            <div className="mr-2">*</div>
            <div>
              <p>
                <span className="font-semibold">Eigenes Design gewünscht?</span>
                <br />
                Wenn Du über rudimentäre Kenntniss in HTML/CSS verfügst, kannst
                Du alle Elemente selbst anpassen und stylen!
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-24 -mt-10" id="howTo">
        <h2 className="md:text-sm">So bindest Du das Widget ein:</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 text-sm">
          <WidgetInfoItem
            index={1}
            text={
              'Füge den Code zum Laden des Widget-Javascripts in die Seite ein. Dieser Code lädt asynchron im Hintergrund, ohne die restliche Seite zu blockieren. Er muss nur einmal pro Seite eingefügt werden, auch wenn mehrere Widgets angezeigt werden.'
            }
            code={`<script>
(function(e,t,n,r,i,s,o){e[i]=e[i]||function(){(e[i].q=e[i].q||[]).push(arguments)}, e[i].l=1*new Date;s=t.createElement(n),o=t.getElementsByTagName(n)[0];s.async=1; s.src=r;o.parentNode.insertBefore(s,o)})(window,document,"script", '${cdn}',"_hb");
</script>`}
          />
          <span>
            <WidgetInfoItem
              index={2}
              text={
                'Füge den HTML-Container in Deiner Seite dort ein, wo das Widget erscheinen soll.'
              }
              code={`<div id='${htmlContainerId}'></div>`}
            />
            <WidgetInfoItem
              index={3}
              text={
                'Füge den Code zum Einbetten Deines Widgets in die Seite ein.'
              }
              code={codeSnippet}
            />
          </span>
        </div>
      </div>
    </>
  )
}

export default WidgetInfoContainer
